import { useCallback, useEffect, useState } from "react";
import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { Browser } from "@capacitor/browser";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import { isNativeCapacitorApp } from "@/integrations/clients/carAuction/nativeAuth";
import { isAppVersionBelow } from "@/lib/native/compareAppVersion";
import {
  fetchNativeAppConfig,
  type NativeAppConfig,
} from "@/services/app/nativeAppConfigService";

export type ForceUpdateState = {
  required: boolean;
  config: NativeAppConfig | null;
  currentVersion: string;
};

export function useNativeForceUpdate(): ForceUpdateState {
  const { isNative } = useNativePlatform();
  const [state, setState] = useState<ForceUpdateState>({
    required: false,
    config: null,
    currentVersion: "0.0.0",
  });

  const check = useCallback(async () => {
    if (!isNative) return;
    try {
      const [info, config] = await Promise.all([
        App.getInfo(),
        fetchNativeAppConfig(),
      ]);

      const currentVersion = info.version || "0.0.0";
      const belowMin = isAppVersionBelow(currentVersion, config.minAppVersion);
      const required = config.forceUpdate && belowMin;

      setState({ required, config, currentVersion });
    } catch {
      /* offline / backend down — do not block app */
    }
  }, [isNative]);

  useEffect(() => {
    if (!isNative) return;

    void check();

    // Re-check when returning to foreground (login / reopen app)
    let handle: { remove: () => Promise<void> } | undefined;
    void App.addListener("appStateChange", ({ isActive }) => {
      if (isActive) void check();
    }).then((h) => {
      handle = h;
    });

    return () => {
      void handle?.remove();
    };
  }, [isNative, check]);

  return state;
}

export async function openNativeStoreUrl(url: string): Promise<void> {
  if (!url) return;
  if (isNativeCapacitorApp()) {
    await Browser.open({ url });
  } else {
    window.open(url, "_blank", "noopener,noreferrer");
  }
}

export function getStoreUrlForPlatform(config: NativeAppConfig): string {
  const platform = Capacitor.getPlatform();
  if (platform === "ios") return config.iosStoreUrl || "";
  return config.androidStoreUrl;
}
